import {
  ConnectorIcon,
  WhiteBoardBoxIcon,
  WhiteBoardFileIcon,
  WhiteboardTextIcon,
} from '@sketchnote/icons';
import { Button, LoaderXs, Panel as Panel2, Tabs } from '@sketchnote/shared-components';
import * as React from 'react';
import {
  Panel,
  TDShapeType,
  TDSnapshot,
  ToolButton,
  ToolButtonWithTooltip,
  Tooltip,
  useTldrawApp,
} from 'sn-wb-tldraw';

import { EraserIcon } from '../Primitives/icons';
import { AttachmentMenu } from './AttachmentMenu';
import { ImageUpload } from './ImageUpload';
import { NoteMenu } from './NoteMenu';
import { PenMenu } from './PenMenu';
import { ShapesMenu } from './ShapesMenu';
import { StampMenu } from './StampMenu';

const activeToolSelector = (s: TDSnapshot) => s.appState.activeTool;
const toolLockedSelector = (s: TDSnapshot) => s.appState.isToolLocked;

export const PrimaryTools = React.memo(function PrimaryTools(): JSX.Element {
  const app = useTldrawApp();
  const [uploadImageModal, setUploadImageModal] = React.useState(false);
  const activeTool = app.useStore(activeToolSelector);
  const [width, setWidth] = React.useState(window.innerWidth);

  const isToolLocked = app.useStore(toolLockedSelector);

  const isSmallScreen = width < 800;

  React.useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleWindowResize);

    // Return a function from the effect that removes the event listener
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const selectSelectTool = React.useCallback(() => {
    app.selectTool('select');
  }, [app]);

  const selectEraseTool = React.useCallback(() => {
    app.selectTool('erase');
  }, [app]);

  const selectDrawTool = React.useCallback(() => {
    app.selectTool(TDShapeType.Draw);
  }, [app]);

  const selectArrowTool = React.useCallback(() => {
    app.selectTool(TDShapeType.Connector);
  }, [app]);

  const selectTextTool = React.useCallback(() => {
    app.selectTool(TDShapeType.Text);
  }, [app]);

  const selectStickyTool = React.useCallback(() => {
    app.selectTool(TDShapeType.Sticky);
  }, [app]);

  const selectUploadAsset = React.useCallback(() => {
    app.openAsset();
  }, [app]);

  const selectSectionTool = React.useCallback(() => {
    app.selectTool(TDShapeType.Section);
  }, [app]);

  return (
    <div
      id="TD-PrimaryTools"
      style={{
        borderRadius: `${isSmallScreen ? '12px' : '16px'}`,
        borderWidth: '1.09375px 1.09375px 1.09375px 0px',
        backdropFilter: 'blur(1.64062px)',
      }}
      className="h-589 smallScreen:w-64 w-92 smallScreen:h-auto bg-white pt-4"
    >
      <div className="flex gap-4 smallScreen:gap-2 flex-col items-center border-gray-200 border-solid">
        <PenMenu activeTool={activeTool} isToolLocked={isToolLocked} />

        <ShapesMenu activeTool={activeTool} isToolLocked={isToolLocked} />

        <NoteMenu activeTool={activeTool} isToolLocked={isToolLocked} />
      </div>

      <div
        className=" border-gray-200 border-dashed  mt-19 mb-20 smallScreen:mt-8 smallScreen:mb-8"
        style={{ borderBottomWidth: '1px' }}
      ></div>

      <div className="flex justify-center flex-col items-center smallScreen:gap-12 smallScreen:mb-8 gap-16 mb-16">
        <div
          // kbd={'9'}
          // label={TDShapeType.Text}
          // isLocked={isToolLocked}
          onClick={selectTextTool}
          id="TD-PrimaryTools-Text"
        >
          <div className="bg-gray-100 rounded-sm h-48 w-48 smallScreen:h-32 smallScreen:w-32 cursor-pointer flex items-center justify-center">
            <Tooltip id="Text" label="Text" side="right">
              <WhiteboardTextIcon
                fill={activeTool === TDShapeType.Text ? '#254DDA' : '#55585E'}
                wrapperClass="flex items-center justify-center transform scale-190 smallScreen:scale-100"
              />
            </Tooltip>
          </div>
        </div>

        <div
          onClick={selectArrowTool}
          // isLocked={isToolLocked}
          id="TD-PrimaryTools-ArrowTopRight"
        >
          <div className="bg-gray-100 rounded-sm h-48 w-48 smallScreen:h-32 smallScreen:w-32 cursor-pointer flex items-center justify-center">
            <Tooltip id="Connector" label="Connector" side="right">
              <ConnectorIcon
                fill={activeTool === TDShapeType.Connector ? '#254DDA' : '#55585E'}
                wrapperClass="flex items-center justify-center transform smallScreen:scale-50"
              />
            </Tooltip>
          </div>
        </div>

        <div
          onClick={selectSectionTool}
          // isLocked={isToolLocked}
          // isActive={activeTool === TDShapeType.Arrow}
          id="TD-PrimaryTools-BoxTopRight"
        >
          <div className="bg-gray-100 rounded-sm h-48 w-48  smallScreen:h-32 smallScreen:w-32  cursor-pointer flex items-center justify-center ">
            <Tooltip id="Section" label="Section" side="right">
              <WhiteBoardBoxIcon
                fill={activeTool === TDShapeType.Section ? '#254DDA' : '#55585E'}
                wrapperClass="flex items-center justify-center transform smallScreen:scale-50"
              />
            </Tooltip>
          </div>
        </div>

        <StampMenu />

        <AttachmentMenu />
      </div>
    </div>
  );
});

//search lessons and files (async paginate)
//swap zIndex (front to Bank) add alignments too
//options to select color inside sticky note similar to text
//remove the section without deleting shapes
